import { NO_PATTERN_BUSINESS_LOGIC } from '@vfde-brix/ws10/core';
import {
    joinArrayWith,
    replacePlaceholders,
} from '@vfde-sails/utils';
import {
    ADDITIONAL_PAGE_OPTIONS,
    RedTariff,
    SalesChannel,
    SALESCHANNEL_FAMILY_FRIENDS,
    SALESCHANNEL_REDPLUS,
    SALESCHANNEL_SOHO,
    YoungTariff,
} from '@vfde-sails/constants';
import { IButtonProperties } from '@vfde-brix/ws10/button';
import { AbstractedTariffWithHardwareOffer } from '@vfde-sails/glados-v2';
import {
    RootState,
    useAppDispatch,
} from '../../../app/store';
import { HighlightBadgeOptions } from '../../../interfaces/page';
import {
    goToBasket,
    selectCustomerConfig,
    selectSkipConnectMore,
} from '../slice';
import {
    selectDataVolume,
    selectTariff,
} from '../selectors';
import { fetchOverlay } from '@vfde-sails/overlay';
import { GIGAKOMBI_OVERLAY_NAME } from '../../../app/constants';
import { CustomerConfig } from '../../../interfaces/state';

/**
 * Helper function to determine if tariff information should be shown
 */
export const shouldShowTariffInformation = (hasSubscription: boolean, isHardwareOnly: boolean): boolean =>
    hasSubscription && !isHardwareOnly;

/**
 * Helper function to determine if monthly price should be shown
 */
export const shouldShowMonthlyPrice = (hasSubscription: boolean, hasInsurance: boolean, isHardwareOnly: boolean): boolean =>
    (hasSubscription || hasInsurance) && !isHardwareOnly;

/**
 * Helper function to determine if VVL Link should be shown
 */
export const shouldShowVvlLink = (salesChannel: SalesChannel) =>
    ![
        SALESCHANNEL_FAMILY_FRIENDS,
        SALESCHANNEL_REDPLUS,
    ].includes(salesChannel);

/**
 * Get price condition
 */
export const getPriceCondition = (hasSubscription: boolean, isHardwareOnly: boolean): string => {
    if (!hasSubscription && !isHardwareOnly) {
        return window[ADDITIONAL_PAGE_OPTIONS].offerSummaryCard.priceCondition;
    }

    return '';
};

/**
 * Get highlight badge text
 */
export const getHighlightBadgeContent = (state: RootState): HighlightBadgeOptions | undefined => {
    const customerConfig = selectCustomerConfig(state);
    const contentKey = customerConfig?.subscriptionId || 'default';
    const {
        hardwareOnly,
        highlightBadges,
        gigakombi,
        familyFriends,
        soho,
        redplus,
    } = window[ADDITIONAL_PAGE_OPTIONS];

    if (customerConfig.isHardwareOnly) {
        return hardwareOnly?.highlightBadge;
    }

    if (customerConfig.salesChannel === SALESCHANNEL_FAMILY_FRIENDS && familyFriends.highlightBadges?.[contentKey]) {
        return familyFriends.highlightBadges[contentKey];
    }

    if (
        customerConfig.salesChannel === SALESCHANNEL_SOHO
        && soho.highlightBadges?.[contentKey]
        && !customerConfig?.isGigakombi
    ) {
        return soho.highlightBadges[contentKey];
    }

    if (customerConfig.salesChannel === SALESCHANNEL_REDPLUS && redplus.highlightBadges?.[contentKey]) {
        return redplus.highlightBadges[contentKey];
    }

    if (customerConfig?.isGigakombi && gigakombi.highlightBadges?.[contentKey]) {
        return gigakombi.highlightBadges[contentKey];
    }

    return highlightBadges?.[contentKey];
};

/**
 * Get headline
 */
export const getHeadline = (name: string, customerConfig: CustomerConfig): string => {
    const { insurance, isHardwareOnly } = customerConfig!;
    const { offerSummaryCard } = window[ADDITIONAL_PAGE_OPTIONS];
    const { insuranceTitle, withAddons } = offerSummaryCard;
    const addons: string[] = [];

    insurance && !isHardwareOnly && addons.push(insuranceTitle);

    return addons.length === 0 ? name : replacePlaceholders(withAddons, {
        name,
        addons: joinArrayWith(addons, ', ', ' &amp; '),
    });
};

/**
 * Get subline text
 */
export const getSubline = (state: RootState): string[] => {
    const { isHardwareOnly } = selectCustomerConfig(state);

    if (isHardwareOnly) {
        return [window[ADDITIONAL_PAGE_OPTIONS].hardwareOnly.offerSummaryCard.tariffSubline];
    }

    const subscription = selectTariff(state);

    if (subscription) {
        const dataVolume = selectDataVolume(state);

        return [
            dataVolume!.value === 0 && !dataVolume!.unlimited
                ? subscription.tariffName
                : `${subscription.tariffName} mit ${dataVolume!.unlimited ? window[ADDITIONAL_PAGE_OPTIONS].offerSummaryCard.unlimitedDataVolume : `${dataVolume!.value} ${dataVolume!.unit}`}`,
        ];
    }

    return [window[ADDITIONAL_PAGE_OPTIONS].offerSummaryCard.tariffSubline];
};

/**
 * Get legal text
 */
export const getLegalText = (state: RootState): string[] => {
    const subscription = selectTariff(state);
    const { isHardwareOnly } = selectCustomerConfig(state);

    const legalText: string[] = [];
    const showTariffInformation = shouldShowTariffInformation(!!subscription, isHardwareOnly);

    if (showTariffInformation) {
        const connectionFee = getConnectionFee(subscription);
        const firstLegalTextLine = [];

        if (!connectionFee) {
            // if there is no oneTimeFee, we should add the 'noConnectionFee' text to the legalText
            firstLegalTextLine.push(window[ADDITIONAL_PAGE_OPTIONS].offerSummaryCard.noConnectionFee);
        }

        firstLegalTextLine.push(window[ADDITIONAL_PAGE_OPTIONS].offerSummaryCard.contractRuntime);
        legalText.push(firstLegalTextLine.filter(Boolean).join(' '));
    }

    return legalText.filter(Boolean);
};

/**
 * Returns the connection fee from the given subscription as number
 * @param subscription The subscription
 */
export const getConnectionFee = (
    subscription: AbstractedTariffWithHardwareOffer<RedTariff | YoungTariff> | null,
): number => {
    const connectionFee = subscription?.priceConnectionFee;
    const hasConnectionFee = !!connectionFee?.gross;

    if (!hasConnectionFee) {
        return 0;
    }

    return connectionFee.gross;
};

/**
 * Get button text and link
 */
export const getPrimaryButtonProps = (state: RootState): Partial<IButtonProperties> => {
    const customerConfig = selectCustomerConfig(state);
    const {
        hardwareOnly,
        offerSummaryCard,
        gigakombi,
        familyFriends,
        redplus,
    } = window[ADDITIONAL_PAGE_OPTIONS];
    const hasSubscription = !!customerConfig?.subscriptionId;
    const buttonProps: Partial<IButtonProperties> = {
        linkHref: window[ADDITIONAL_PAGE_OPTIONS].tariffOverview[customerConfig.salesChannel!],
        stdContent: offerSummaryCard.goToTariffOverviewButton,
    };
    const dispatch = useAppDispatch();

    if (customerConfig.isHardwareOnly || hasSubscription) {
        buttonProps.stdContent = hardwareOnly.offerSummaryCard.primaryButtonText;
        buttonProps.linkHref = hardwareOnly.offerSummaryCard.primaryButtonLink;
        buttonProps.business = NO_PATTERN_BUSINESS_LOGIC;
    }

    if (customerConfig.salesChannel === SALESCHANNEL_FAMILY_FRIENDS && hasSubscription && !customerConfig.isHardwareOnly) {
        buttonProps.stdContent = familyFriends.offerSummaryCard.primaryButtonText;
        buttonProps.linkHref = familyFriends.offerSummaryCard.primaryButtonLink;
        buttonProps.business = NO_PATTERN_BUSINESS_LOGIC;
    }

    if (customerConfig.salesChannel === SALESCHANNEL_REDPLUS && hasSubscription && !customerConfig.isHardwareOnly) {
        buttonProps.stdContent = redplus.offerSummaryCard.primaryButtonText;
        buttonProps.linkHref = undefined;
        buttonProps.business = {
            onClick: /* istanbul ignore next */ () => {
                dispatch(goToBasket());
            },
        };
    }

    if (customerConfig?.isGigakombi && !customerConfig.isHardwareOnly && hasSubscription) {
        buttonProps.stdContent = gigakombi.offerSummaryCard.basketButtonText;
        buttonProps.linkHref = undefined;
        buttonProps.business = {
            onClick: /* istanbul ignore next */ () => {
                const url = window[ADDITIONAL_PAGE_OPTIONS].gigakombi.offerSummaryCard.gigakombiOverlay;

                dispatch(fetchOverlay({
                    url,
                    context: {
                        trackingData: {
                            trackingName: GIGAKOMBI_OVERLAY_NAME,
                        },
                    },
                }));
            },
        };
    }

    const skipConnectMore = selectSkipConnectMore(state);

    if (
        (!customerConfig.isHardwareOnly && customerConfig?.isGigakombiEligible)
        || skipConnectMore
    ) {
        buttonProps.stdContent = offerSummaryCard.defaultBasketButtonText;
        buttonProps.linkHref = undefined;
        buttonProps.business = {
            onClick: /* istanbul ignore next */ () => {
                dispatch(goToBasket());
            },
        };
    }

    return buttonProps;
};
